/* eslint-disable react/jsx-props-no-spreading */
import { useState, useCallback, useRef } from 'react';
import { styled } from 'styled-components';
import useOutside from '../../../reports/components/hooks/useOutside';
import useToggle from '../../../reports/components/hooks/useToogle';
import { DefaultBoldTextStyle } from '../../../reports/components/Schools/Styles';
import {
  SortAscIcon,
  SortDescIcon,
} from '../../../reports/components/Schools/Usage/Icons';

export const SortTypes = {
  ByStudentNameDown: { id: 0, text: 'Sort by student name' },
  ByStudentNameUp: { id: 1, text: 'Sort by student name' },
  ByTimeSpentDown: { id: 2, text: 'Sort by time spent' },
  ByTimeSpentUp: { id: 3, text: 'Sort by time spent' },
  ByNumberOfTasksCompletedDown: {
    id: 4,
    text: 'Sort by number of tasks completed',
  },
  ByNumberOfTasksCompletedUp: {
    id: 5,
    text: 'Sort by number of tasks completed',
  },
  ByAccuracyDown: { id: 6, text: 'Sort by accuracy' },
  ByAccuracyUp: { id: 7, text: 'Sort by accuracy' },
  ByNumberOfTasksStruggleDown: {
    id: 8,
    text: 'Sort by number of struggle tasks',
  },
  ByNumberOfTasksStruggleUp: {
    id: 9,
    text: 'Sort by number of struggle tasks',
  },
};

export const SortDirections = {
  None: 0,
  Asc: 1,
  Desc: 2,
};

const Container = styled.div``;

const SortMenuRow = styled.div`
  font-size: 16px;
  line-height: 24px;
  & svg {
    vertical-align: middle;
    display: inline-block;
    margin-right: 8px;
  }
`;

const SortMenuRowActive = styled(SortMenuRow)`
  ${DefaultBoldTextStyle}
  margin-bottom: 15px;
  width: fit-content;
  color: ${({ isActive }) => (isActive ? '#1786C7' : '#42abe9')};
  && path {
    fill: ${({ isActive }) => (isActive ? '#1786C7' : '#42abe9')};
  }
  &:hover {
    color: #1786c7;
    & path {
      fill: #1786c7;
    }
    cursor: pointer;
  }
`;

const SortMenuRowSelect = styled(SortMenuRow)`
  display: ${({ hide }) => (hide ? 'none' : 'flex')};
  height: 40px;
  align-items: center;
  border-radius: 8px;
  padding-left: 10px;

  &:hover {
    background-color: #e3f2fc;
    cursor: pointer;
  }
`;

const Menu = styled.div`
  padding: 8px;
  width: 338px;
  border-radius: 8px;
  position: absolute;
  background-color: #ffffff;
  z-index: 51;
  box-shadow: 0px 1px 8px rgb(0 0 0 / 17%);
`;

const TeacherSortMenu = ({ onChange, ...rest }) => {
  const [activeSortItem, setActiveSortItem] = useState(
    SortTypes.ByStudentNameDown
  );
  const [isTooggle, toggle] = useToggle(false);
  const onSortItem = useCallback(
    (item) => {
      setActiveSortItem(item);
      toggle();
      onChange({
        sortType: item,
        direction: ['0', '2', '4', '6', '8'].includes(item.id.toString())
          ? SortDirections.Desc
          : SortDirections.Asc,
      });
    },
    [onChange, toggle]
  );

  const sortMenuRef = useRef(null);

  useOutside(sortMenuRef, () => {
    if (isTooggle) toggle();
  });

  return (
    <Container ref={sortMenuRef} {...rest}>
      <SortMenuRowActive onClick={toggle}>
        {['0', '2', '4', '6', '8'].includes(activeSortItem.id.toString()) ? (
          <SortAscIcon />
        ) : (
          <SortDescIcon />
        )}
        {activeSortItem.text}
      </SortMenuRowActive>
      {isTooggle && (
        <Menu>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByStudentNameDown.id}
            onClick={() => onSortItem(SortTypes.ByStudentNameDown)}
          >
            <SortAscIcon />
            {SortTypes.ByStudentNameDown.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByStudentNameUp.id}
            onClick={() => onSortItem(SortTypes.ByStudentNameUp)}
          >
            <SortDescIcon />
            {SortTypes.ByStudentNameUp.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByTimeSpentDown.id}
            onClick={() => onSortItem(SortTypes.ByTimeSpentDown)}
          >
            <SortAscIcon />
            {SortTypes.ByTimeSpentDown.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByTimeSpentUp.id}
            onClick={() => onSortItem(SortTypes.ByTimeSpentUp)}
          >
            <SortDescIcon />
            {SortTypes.ByTimeSpentUp.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={
              activeSortItem.id === SortTypes.ByNumberOfTasksCompletedDown.id
            }
            onClick={() => onSortItem(SortTypes.ByNumberOfTasksCompletedDown)}
          >
            <SortAscIcon />
            {SortTypes.ByNumberOfTasksCompletedDown.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByNumberOfTasksCompletedUp.id}
            onClick={() => onSortItem(SortTypes.ByNumberOfTasksCompletedUp)}
          >
            <SortDescIcon />
            {SortTypes.ByNumberOfTasksCompletedUp.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByAccuracyDown.id}
            onClick={() => onSortItem(SortTypes.ByAccuracyDown)}
          >
            <SortAscIcon />
            {SortTypes.ByAccuracyDown.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByAccuracyUp.id}
            onClick={() => onSortItem(SortTypes.ByAccuracyUp)}
          >
            <SortDescIcon />
            {SortTypes.ByAccuracyUp.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={
              activeSortItem.id === SortTypes.ByNumberOfTasksStruggleDown.id
            }
            onClick={() => onSortItem(SortTypes.ByNumberOfTasksStruggleDown)}
          >
            <SortAscIcon />
            {SortTypes.ByNumberOfTasksStruggleDown.text}
          </SortMenuRowSelect>
          <SortMenuRowSelect
            hide={activeSortItem.id === SortTypes.ByNumberOfTasksStruggleUp.id}
            onClick={() => onSortItem(SortTypes.ByNumberOfTasksStruggleUp)}
          >
            <SortDescIcon />
            {SortTypes.ByNumberOfTasksStruggleUp.text}
          </SortMenuRowSelect>
        </Menu>
      )}
    </Container>
  );
};

export { TeacherSortMenu };
